import React, { useCallback, useContext, useMemo, useState } from "react";
import { VenueContext } from "contexts";
import { useApi, useMount } from "hooks";
import { ModuleWrapper } from "components/fragments";
import { HeaderA } from "components/headers";
import { Panel, Title, Text, Pill, Table, Field, Select, RangePicker, Skeleton } from "components/commons";
import lang from "translations";
import * as moment from "moment";
import { toApiDateTimeFormat } from "services";
import {
  getResourceOccupancyGraph,
  getResourceStatusKpi,
  getResourceStatusTimeline,
  getResources,
} from "apis/resource.api";
import {
  resourceOccupancyGraph,
  resourceStatusKpi,
  resourceStatusTimelineEntry,
} from "mappers/resource-status-history.mapper";

const splitTypeOptions = [
  { text: lang.hour, value: "HOUR" },
  { text: lang.day, value: "DAY" },
  { text: lang.month, value: "MONTH" },
];

// Resource occupancy/turnover reporting -- resource_status_history/resource_current_state
// were previously write-only (no read surface at all). Distinct from the plain Resource
// CRUD pages: this is a reporting view, not a resource editor. State names are venue-
// specific free text, so the occupancy graph renders as a table with one dynamic column
// per observed state rather than assuming fixed categories like "OCCUPIED"/"AVAILABLE".
const ResourceInsights = () => {
  const { venue } = useContext(VenueContext);
  const { venueId } = venue;

  const [dateRange, setDateRange] = useState([moment().subtract(7, "days").startOf("day"), moment().endOf("day")]);
  const [splitType, setSplitType] = useState("DAY");
  const [selectedResourceId, setSelectedResourceId] = useState(null);

  const { request: requestResources, mappedData: resourceOptions = [] } = useApi({
    api: getResources,
    isArray: true,
    mapper: { text: { key: "defaultName" }, value: { key: "resourceId" } },
    returnMappedData: true,
    handleOwnError: true,
  });

  const { request: requestGraph, loading: loadingGraph, mappedData: graphData } = useApi({
    api: getResourceOccupancyGraph,
    mapper: resourceOccupancyGraph,
    handleOwnError: true,
  });

  const { request: requestKpi, loading: loadingKpi, mappedData: kpiData } = useApi({
    api: getResourceStatusKpi,
    mapper: resourceStatusKpi,
    handleOwnError: true,
  });

  const {
    request: requestTimeline,
    loading: loadingTimeline,
    mappedData: timelineData = [],
  } = useApi({
    api: getResourceStatusTimeline,
    isArray: true,
    mapper: resourceStatusTimelineEntry,
    returnMappedData: true,
    handleOwnError: true,
  });

  const fetchAll = useCallback(
    (range = dateRange, split = splitType) => {
      const startDateTime = toApiDateTimeFormat(range[0]);
      const endDateTime = toApiDateTimeFormat(range[1], true);
      requestGraph({ venueId, startDateTime, endDateTime, splitType: split });
      requestKpi({ venueId, startDateTime, endDateTime });
    },
    [dateRange, splitType, venueId, requestGraph, requestKpi]
  );

  useMount(() => {
    requestResources({ venueId });
    fetchAll();
  });

  const onDateRangeChange = useCallback(
    (name, value) => {
      setDateRange(value);
      fetchAll(value, splitType);
    },
    [fetchAll, splitType]
  );

  const onSplitTypeChange = useCallback(
    (name, { value }) => {
      setSplitType(value);
      fetchAll(dateRange, value);
    },
    [fetchAll, dateRange]
  );

  const onResourceChange = useCallback(
    (name, { value }) => {
      setSelectedResourceId(value);
      if (value) {
        requestTimeline({
          resourceId: value,
          startDateTime: toApiDateTimeFormat(dateRange[0]),
          endDateTime: toApiDateTimeFormat(dateRange[1], true),
        });
      }
    },
    [dateRange, requestTimeline]
  );

  const graphColumns = useMemo(() => {
    const stateKeys = graphData.stateKeys || [];
    return [
      { text: lang.date, key: "bucketKey" },
      ...stateKeys.map((state) => ({ text: state, key: state, custom: true })),
    ];
  }, [graphData.stateKeys]);

  const graphRows = useMemo(() => {
    const stateKeys = graphData.stateKeys || [];
    return (graphData.rows || []).map((row) => {
      const formatted = { bucketKey: row.bucketKey };
      stateKeys.forEach((state) => {
        const seconds = row[state];
        formatted[state] = seconds ? <Text>{Math.round(seconds / 60)}m</Text> : <Text color="text-gray-400">-</Text>;
      });
      return formatted;
    });
  }, [graphData]);

  const timelineColumns = [
    { text: lang.status, key: "fromState" },
    { text: "->", key: "toState" },
    { text: lang.date, key: "changedAt" },
    { text: lang.duration, key: "previousStateDurationSeconds" },
    { text: lang.reason, key: "reason" },
  ];

  return (
    <ModuleWrapper
      header={<HeaderA title={<Title xl>{lang.resourceInsights}</Title>} description={lang.resourceInsightsDescription} />}
    >
      <Panel className="mb-md">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Field filterLabel={lang.dateRange}>
            <RangePicker name="dateRange" value={dateRange} onChange={onDateRangeChange} />
          </Field>
          <Field filterLabel={lang.granularity}>
            <Select name="splitType" value={splitType} options={splitTypeOptions} onChange={onSplitTypeChange} />
          </Field>
          <Field filterLabel={lang.resource}>
            <Select
              name="resourceId"
              value={selectedResourceId}
              options={[{ text: lang.allResources, value: null }, ...resourceOptions]}
              onChange={onResourceChange}
              placeholder={lang.allResources}
            />
          </Field>
        </div>
      </Panel>

      <Panel className="mb-md">
        <Title className="mb-md">{lang.kpiSummary}</Title>
        {loadingKpi ? (
          <Skeleton />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label={lang.totalTransitions}>
              <Text size="text-xl" strong>
                {kpiData.totalTransitionCount ?? 0}
              </Text>
            </Field>
            <Field label={lang.currentStateDistribution}>
              <div className="flex flex-wrap gap-2">
                {Object.entries(kpiData.currentStateDistribution || {}).map(([state, count]) => (
                  <Pill key={state} size="text-xs">
                    {state}: {count}
                  </Pill>
                ))}
              </div>
            </Field>
            <Field label={lang.avgDurationByState} className="col-span-2">
              <div className="flex flex-wrap gap-2">
                {Object.entries(kpiData.avgDurationSecondsByState || {}).map(([state, avgSeconds]) => (
                  <Pill key={state} size="text-xs">
                    {state}: {Math.round(avgSeconds / 60)}m avg
                  </Pill>
                ))}
              </div>
            </Field>
          </div>
        )}
      </Panel>

      <Panel className="mb-md">
        <Title className="mb-md">{lang.occupancyGraph}</Title>
        <div className="overflow-x-auto overflow-hidden">
          <Table loading={loadingGraph} data={graphRows} columns={graphColumns} minWidth="700px" />
        </div>
      </Panel>

      {selectedResourceId && (
        <Panel>
          <Title className="mb-md">{lang.resourceTimeline}</Title>
          <div className="overflow-x-auto overflow-hidden">
            <Table loading={loadingTimeline} data={timelineData} columns={timelineColumns} minWidth="700px" />
          </div>
        </Panel>
      )}
    </ModuleWrapper>
  );
};

export default ResourceInsights;
